import React, { createContext, useContext, useState, useEffect } from "react";
import { collection, doc, setDoc, deleteDoc, onSnapshot } from "firebase/firestore";
import { db as firestoreDb } from "./firebase";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

interface WishlistContextType {
  wishlist: string[]; // array of product IDs
  toggleWishlist: (productId: string) => Promise<void>;
  isInWishlist: (productId: string) => boolean;
  loading: boolean;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export function WishlistProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setWishlist([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const wishlistRef = collection(firestoreDb, "users", user.uid, "wishlist");
    const unsubscribe = onSnapshot(wishlistRef, (snapshot) => {
      const ids = snapshot.docs.map(doc => doc.id);
      setWishlist(ids);
      setLoading(false);
    }, (error) => {
      console.error("Error listening to wishlist:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const toggleWishlist = async (productId: string) => {
    if (!user) {
      toast.error("Debes iniciar sesión para guardar en favoritos");
      return;
    }

    const isAdded = wishlist.includes(productId);
    try {
      const itemRef = doc(firestoreDb, "users", user.uid, "wishlist", productId);
      if (isAdded) {
        await deleteDoc(itemRef);
        toast.success("Eliminado de favoritos");
      } else {
        await setDoc(itemRef, {
          productId,
          addedAt: new Date().toISOString()
        });
        toast.success("Agregado a favoritos");
      }
    } catch (error) {
      console.error("Error toggling wishlist:", error);
      toast.error("Error al actualizar favoritos");
    }
  };

  const isInWishlist = (productId: string) => {
    return wishlist.includes(productId);
  };

  return (
    <WishlistContext.Provider value={{ wishlist, toggleWishlist, isInWishlist, loading }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
}
